import {
	ActionFormatterData,
	ArgFormatterData, BarFormatterData,
	BooleanFormatterData, ByteSizeFormatterData,
	CellContext,
	CellFormatter,
	CellFormatterData, ConstFormatterData, DateFormatterData, DefaultFormatterData,
	DumpFormatterData, DurationFormatterData, DurationUnit, EnumFormatterData, ExpandFormatterData, FormatterType,
	FragmentData, I18nFormatterData, IconFormatterData, IfFormatterData, ImageFormatterData, IndexFormatterData, JoinFormatterData,
	limitHtml,
	limitText, ListFormatterData, MapFormatterData, MoneyFormatterData, NumberFormatterData, PackFormatterData,
	PackFormatterRule, PercentFormatterData, PopFormatterData, SlotFactory,
	StringFormatterData, TagFormatterData, UserFormatterData, WithFormatterData,
	ConcatFormatterData, LinkFormatterData, WrapFormatterData, ActionParam
} from "@t/agenda/CellFormatter.ts";
import {testCondition} from "@fd/containers/agenda/Condition.ts";
import {parseDate} from "@t/util/DateUtil.ts";
import {Icon, StyledIcon} from "anneca-symbols";
import {Column, ColumnType, GROUP_LIST_NAME, USER_GROUP_LIST_NAME, USER_LIST_NAME} from "@t/agenda/Columns.ts";
import {propsToStyle, StoreList, StoreListItem, useListStore} from "@/store/ListStore.ts";
import {CSSProperties, Ref, h, type VNodeChild} from "vue";
import {getter} from "@fd/Part.ts";
import {injectEnv} from "@fd/Environment.ts";
import {FormEnvironment} from "@fd/Types.ts";
import {type} from "jquery";


export function formatterOfColumn(c:Column) {
	if (!c._formatter) {
		c._formatter = formatterOf(c.format ? c.format : buildDefaultFormatter(c));
	}
	return c._formatter;
}

export function formatterOf(f:CellFormatterData):CellFormatter {
    switch (f.type) {
		case "DUMP": return new DumpFormatter(f as DumpFormatterData);
		case "STRING": return new StringFormatter(f as StringFormatterData);
		case "DATE": return new DateFormatter(f as DateFormatterData);
		case "BOOLEAN": return new BooleanFormatter(f as BooleanFormatterData);
		case "NUMBER": return new NumberFormatter(f as NumberFormatterData);
		case "MONEY": return new MoneyFormatter(f as MoneyFormatterData);
		case "PERCENT": return new PercentFormatter(f as PercentFormatterData);
		case "BAR": return new BarFormatter(f as BarFormatterData);
		case "BYTE_SIZE": return new ByteSizeFormatter(f as ByteSizeFormatterData);
		case "CONST": return new ConstFormatter(f as ConstFormatterData);
		case "JOIN": return new JoinFormatter(f as JoinFormatterData);
		case "ICON": return new IconFormatter(f as IconFormatterData);
		case "IMAGE": return new ImageFormatter(f as ImageFormatterData);
		case "INDEX": return new IndexFormatter(f as IndexFormatterData);
		case "MAP": return new MapFormatter(f as MapFormatterData);
		case "LIST": return new ListFormatter(f as ListFormatterData);
		case "ARG": return new ArgFormatter(f as ArgFormatterData);
		case "IF": return new IfFormatter(f as IfFormatterData);
		case "POP": return new PopFormatter(f as PopFormatterData);
		case "WITH": return new WithFormatter(f as WithFormatterData);
		case "USER": return new UserFormatter(f as UserFormatterData);
		case "ACTION": return new ActionFormatter(f as ActionFormatterData);
		case "DEFAULT": return new DefaultFormatter(f as DefaultFormatterData);
		case "TAG": return new TagFormatter(f as TagFormatterData);
		case "DURATION": return new DurationFormatter(f as DurationFormatterData);
		case "PACK": return new PackFormatter(f as PackFormatterData);
		case "ENUM": return new EnumFormatter(f as EnumFormatterData);
		case "I18N": return new I18Formatter(f as I18nFormatterData);
		case "EXPAND": return new ExpandFormatter(f as ExpandFormatterData);
		case "CONCAT": return new ConcatFormatter(f as ConcatFormatterData);
		case "LINK": return new LinkFormatter(f as LinkFormatterData);
		case "WRAP": return new WrapFormatter(f as WrapFormatterData);
	}
    throw "Invalid formatter type "+f.type;
}

export function buildDefaultFormatter(col: Column):CellFormatterData {
	switch (col.type) {
		case ColumnType.STRING: return col.listName ? <ListFormatterData>{ type: "LIST" } : <StringFormatterData>{ type: "STRING", limit: 500 };
		case ColumnType.TEXT:
		case ColumnType.WORD:
			return <StringFormatterData>{ type: "STRING", limit: 500 };
		case ColumnType.HTML:
			return <StringFormatterData>{ type: "STRING", html:true, limit: 500 };
		case ColumnType.DATE:
		case ColumnType.DATETIME:
		case ColumnType.DATE_AND_TIME:
		case ColumnType.TIME:
			return <DateFormatterData>{ type: "DATE" };
		case ColumnType.INT:
			return col.listName ? <ListFormatterData>{ type: "LIST" } : <NumberFormatterData>{ type: "NUMBER" };
		case ColumnType.LONG:
		case ColumnType.FLOAT:
			return <NumberFormatterData>{ type: "NUMBER" };
		case ColumnType.BOOLEAN:
			return <BooleanFormatterData>{ type: "BOOLEAN" };
		case ColumnType.USER:
			return <UserFormatterData>{ type: "USER" };
		case ColumnType.FULLTEXT:
			return <JoinFormatterData>{
				type: "JOIN",
				input: <ExpandFormatterData>{
					type: "EXPAND",
					format: <UserFormatterData>{
						type: "USER"
					}
				},
				separator: <ConstFormatterData>{
					type: "CONST",
					text: ", "
				}
			};
		case ColumnType.PARTY:
		case ColumnType.OPPORTUNITY:
		case ColumnType.OFFER:
		case ColumnType.BUSCASE:
		case ColumnType.BCITEM:
		case ColumnType.TASK:
		case ColumnType.EVENT:
		case ColumnType.PROBLEM:
		case ColumnType.REGION:
		case ColumnType.CAMPAIGN:
		case ColumnType.PRODUCT:
			return <IfFormatterData>{
				type: "IF",
				trueFormat: <NumberFormatterData>{ type: "NUMBER" }
			};
		case ColumnType.SET:
			return col.listName
				? <ExpandFormatterData>{
					type: "EXPAND",
					format: <JoinFormatterData>{
						type: "JOIN",
						input: <ListFormatterData>{ type: "LIST" },
						separator: <ConstFormatterData>{
							type: "CONST",
							text: ", "
						}
					}
				}
				: <ExpandFormatterData>{
					type: "EXPAND",
					format: <JoinFormatterData>{
						type: "JOIN",
						input: <StringFormatterData>{ type: "STRING", limit: 64 },
						separator: <ConstFormatterData>{
							type: "CONST",
							text: ", "
						}
					}
				}
		default:
			return <ConstFormatterData>{
				type: "CONST",
				text: "UNSUPPORTED type `"+col.type+"`"
			}
	}
}


function htmlToVNode(html:string):VNodeChild {
	return html;
}

class DumpFormatter implements CellFormatter {
	private readonly sub: CellFormatter;
	private readonly subType: FormatterType;

	constructor(f:DumpFormatterData) {
		this.sub = formatterOf(f.format);
		this.subType = f.format.type;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		const args:string[] = [];
		for (let d of data) {
			if (typeof d === 'undefined') args.push("undefined");
			else if (d === null) args.push("null");
			else if (typeof d === 'boolean') args.push(d ? "true" : "false");
			else if (typeof d === 'number') args.push(d.toString());
			else if (typeof d === 'string') args.push("'"+d+"'");
			else args.push(JSON.stringify(d));
		}
		console.log(this.subType + "(" + args.join(", ") + ")");
		return this.sub.format(ctx, ...data);
	}
}

class StringFormatter implements CellFormatter {
	private readonly safeHtml:boolean;
	private readonly limit: number;

	constructor(f:StringFormatterData) {
		this.safeHtml = !!f.html;
		this.limit = f.limit || 0;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let d of data) {
				if (d == null) continue;
				let txt = d.toString();
				if (this.limit > 0) {
					if (this.safeHtml) txt = limitHtml(txt, this.limit);
					else txt = limitText(txt, this.limit);
				}
				out.push(this.safeHtml ? htmlToVNode(txt) : txt);
			}
			return out;
		};
	}
}

const DATE_OPS:Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "numeric",
    year: "numeric"
};

const DATE_TIME_OPS:Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "numeric",
    weekday: "short"
};

class DateFormatter implements CellFormatter {
	private readonly pattern: string|null;

	constructor(f:DateFormatterData) {
		this.pattern = f.pattern || null;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let d of data) {
				if (d == null) continue;
				let wholeDay:boolean;
				let date:Date;
				if (typeof d === "number") {
					// milliseconds
					date = new Date(d);
					wholeDay = false;
				} else if (typeof d === "string") {
					// ISO format expected
					const parsed = parseDate(d);
					if (parsed.date == null) continue;
					date = parsed.date;
					wholeDay = parsed.wholeDay;
				} else if (d instanceof Date) {
					// JS Date
					date = d;
					wholeDay = false;
				}
				if (date == null) continue;
				let ops;
				if (wholeDay) {
					ops = DATE_OPS;
				} else {
					ops = DATE_TIME_OPS;
				}
				let formatted = new Intl.DateTimeFormat(navigator.language, ops).format(date);
				out.push(formatted);
			}
			return out;
		};
	}
}

function iconNode(si:StyledIcon):VNodeChild {
	return h("span", { class: ['fi', si.clazz], style: si.style, innerHTML: si.entity });
}

function fi(name:string):VNodeChild {
	return iconNode(Icon.parse(name));
}

const TRUE_ICON:StyledIcon = Icon.parse("yes{color:var(--green)}");
const TRUE_FRAGMENT:VNodeChild = iconNode(TRUE_ICON);

class BooleanFormatter implements CellFormatter {

	private readonly trueIcon: string|null;
	private readonly falseIcon: string|null;

	private readonly trueFragment:VNodeChild;
	private readonly falseFragment:VNodeChild;


	constructor(f:BooleanFormatterData) {
		this.trueIcon = f.trueIcon || null;
		this.falseIcon = f.falseIcon || null;

		if (this.trueIcon == null) this.trueFragment = TRUE_FRAGMENT;
		else this.trueFragment = fi(this.trueIcon);

		if (this.falseIcon == null) this.falseFragment = null;
		else this.falseFragment = fi(this.falseIcon);
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let d of data) {
				if (typeof d === "boolean") {
					if (d) out.push(this.trueFragment);
					else if (this.falseFragment) out.push(this.falseFragment);
				}
			}
			return out;
		};
	}
}

const NAN_FRAGMENT:VNodeChild = h('span', { style: { color: "var(--red)"} }, ['NaN']);

class NumberFormatter implements CellFormatter {
	constructor(f:NumberFormatterData) {
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let d of data) {
				if (d == null) continue;
				if (typeof d !== "number" || Number.isNaN(d)) out.push(NAN_FRAGMENT);
				else {
					const result = new Intl.NumberFormat(navigator.language).format(d);
					out.push(result);
				}
			}
			return out;
		};
	}
}

class MoneyFormatter implements CellFormatter {

	private readonly withCurrencySymbol:boolean;

	constructor(f:MoneyFormatterData) {
		this.withCurrencySymbol = f.symbol || false;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			let amount:number;
			let currency:string;
			if (data.length == 0) amount = 0;
			else if (typeof data[0] === 'number') {
				if (Number.isNaN(data[0])) return [ NAN_FRAGMENT ];
				amount = data[0];
			}
			else amount = Number.parseFloat(data[0].toString());

			if (data.length > 1) currency = data[1].toString();

			// Detect locale safely (browser / non-browser)
			const locale = (typeof navigator !== 'undefined' && navigator.language) || 'cs-CZ';
			const includeSymbol = this.withCurrencySymbol && data.length > 1
			try {
				const options:Intl.NumberFormatOptions = {
					style: includeSymbol ? 'currency' : 'decimal',
					currency: includeSymbol ? currency : undefined,
					minimumFractionDigits: 0,
					maximumFractionDigits: 20
				};

				return [new Intl.NumberFormat(locale, options).format(amount)];
			} catch (e) {
				// Fallback for unsupported currencies (e.g. BTC)
				const formatted = new Intl.NumberFormat(locale, {
					minimumFractionDigits: 0,
					maximumFractionDigits: 20
				}).format(amount);

				return [includeSymbol ? `${formatted} ${currency}` : formatted];
			}
		};
	}
}


class PercentFormatter implements CellFormatter {

	private readonly divider:number;

	constructor(f:PercentFormatterData) {
		this.divider = f.divider || 1;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let d of data) {
				if (d == null) continue;
				if (typeof d !== "number" || Number.isNaN(d)) out.push(NAN_FRAGMENT);
				else {
					const result = new Intl.NumberFormat(navigator.language, {
						style: "percent",
						minimumFractionDigits: 0,
						maximumFractionDigits: 2
					}).format(d / this.divider);
					out.push(result);
				}
			}
			return out;
		};
	}
}


class BarFormatter implements CellFormatter {

	private readonly width:number;
	private readonly denominator:number;

	constructor(f:BarFormatterData) {
		this.width = f.width || 64
		this.denominator = f.denominator || 0;
		if (this.width < 18) this.width = 64;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			if (data.length == 0 || data[0] == null || typeof data[0] !== 'number') return out;
			const numerator:number = data[0];
			let denominator:number;
			if (data.length > 1 && data[1] != null && typeof data[1] === 'number') denominator = data[1];
			else if (this.denominator !== 0) denominator = this.denominator;
			else denominator = 0;

			const maxBarWidth:number = this.width - 4;	// inner bar width;
			let barWidth:number = 0;	// inner bar width;
			let bg:string = null;
			let showBar:boolean;
			if (denominator != 0) {
				let ratio:number = Math.abs(numerator / denominator);
				showBar = true;
				barWidth = Math.round(ratio * maxBarWidth);
				if (barWidth == 0) showBar = false;
				if (barWidth == maxBarWidth) bg="var(--green)";
				else if (barWidth < 6) {
					barWidth = 6;
					bg = "var(--orange)";
				} else if (barWidth > maxBarWidth) {
					barWidth = maxBarWidth;
					bg = "linear-gradient(to right, var(--green) 0%, var(--green) 66%, var(--red))";
				} else {
					const perc = Math.round(ratio * 100);
					bg = "color-mix(in srgb, var(--orange), var(--green) "+perc+"%)";
				}
			} else {
				showBar = false;
			}

			const barStyle:CSSProperties = { width: this.width+"px" };
			const barContent = [];
			if (showBar) {
				const trackStyle:CSSProperties = { width: barWidth+"px" };
				if (bg) trackStyle['background'] = bg;
				barContent.push(h("span", { 'class':'track', style:trackStyle }));
			}

			out.push(h('span', { 'class': 'cell-bar', style: barStyle}, barContent));
			return out;
		};
	}
}

function formatByteSize(size: number): string {
	if (size < 0) return "-"; // unable to format

	if (size < 1024) return `${Math.trunc(size)} B`;

	const kilos = Math.trunc(size / 1024);
	if (kilos < 1024) return `${kilos} kB`;

	const megas = kilos / 1024;
	if (megas < 1024) return `${simleRound(megas)} MB`;

	const gigas = megas / 1024;
	return `${simleRound(gigas)} GB`;
}

function simleRound(value: number): string {
	return value.toFixed(2);
}

class ByteSizeFormatter implements CellFormatter {
	constructor(f:ByteSizeFormatterData) {
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let d of data) {
				if (d == null) continue;
				if (typeof d !== "number" || Number.isNaN(d)) out.push(NAN_FRAGMENT);
				else {
					const result = formatByteSize(d);
					out.push(result);
				}
			}
			return out;
		};
	}
}

class ConstFormatter implements CellFormatter {
	private readonly text:string;
	private readonly safeHtml:boolean;

	constructor(f:ConstFormatterData) {
		this.text = f.text;
		this.safeHtml = !!f.html;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => [this.safeHtml ? htmlToVNode(this.text) : this.text];
	}
}

class JoinFormatter implements CellFormatter {
	private readonly input:CellFormatter;
	private readonly separator:CellFormatter|null;
	private readonly prefix:CellFormatter|null;
	private readonly suffix:CellFormatter|null;
	private readonly ifEmpty:CellFormatter|null;

	constructor(f:JoinFormatterData) {
		this.input = formatterOf(f.input);
		this.separator = f.separator ? formatterOf(f.separator) : null;
		this.prefix = f.prefix ? formatterOf(f.prefix) : null;
		this.suffix = f.suffix ? formatterOf(f.suffix) : null;
		this.ifEmpty = f.ifEmpty ? formatterOf(f.ifEmpty) : null;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const raw:VNodeChild[] = this.input.format(ctx, ...data)();
			const rawLen = raw.length;

			if (rawLen == 0) {
				if (this.ifEmpty != null) return this.ifEmpty.format(ctx, ...data)();
				const list:VNodeChild[] = [];
				if (this.prefix != null) list.push(...this.prefix.format(ctx, ...data)());
				if (this.suffix != null) list.push(...this.suffix.format(ctx, ...data)());
				return list;
			}
			const list:VNodeChild[] = [];
			if (this.prefix != null) list.push(...this.prefix.format(ctx, ...data)());
			for (let i = 0; i < raw.length; i++) {
				if (i > 0 && this.separator != null) list.push(...this.separator.format(ctx, ...data)());
				list.push(raw[i]);
			}
			if (this.suffix != null) list.push(...this.suffix.format(ctx, ...data)());
			return list;
		};
	}
}


class IconFormatter implements CellFormatter {
	constructor(f:IconFormatterData) {
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let d of data) {
				const iconName:string = d == null ? "empty-18" : d.toString();
				out.push(fi(iconName));
			}
			return out;
		};
	}
}


class ImageFormatter implements CellFormatter {
	private readonly maxWidth: number|null;
	private readonly maxHeight: number|null;

	constructor(f:ImageFormatterData) {
		this.maxWidth = f.maxWidth || null;
		this.maxHeight = f.maxHeight || null;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			if (data.length > 0 && data[0] !=null) {
				const url = data[0].toString();
				const alt = data.length > 1 ? data[1].toString() : "image " + ctx.primaryKey;
				let style:CSSProperties = undefined;
				if (this.maxWidth > 0 || this.maxHeight > 0) {
					style = {};
					if (this.maxWidth > 0) style.maxWidth = this.maxWidth + "px";
					if (this.maxHeight > 0) style.maxHeight = this.maxHeight + "px";
				}
				out.push(h('img', { src: url, alt: alt, style: style }));
			}
			return out;
		};
	}
}

class IndexFormatter implements CellFormatter {
	private readonly indices: number[]|null;
	private readonly sub: CellFormatter;

	constructor(f:IndexFormatterData) {
		this.indices = f.indices || null;
		this.sub = formatterOf(f.format);
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		if (this.indices == null) return this.sub.format(ctx, ...data);
		else {
			// Format only selected indexes
			const subset:any[] = [];
			for (let i = 0; i < this.indices.length; i++) {
				const idx = this.indices[i];
				if (idx >= 0 && idx < data.length) subset[i] = data[idx];
				else subset[i] = null;	// no data for this slot
			}
			return this.sub.format(ctx, ...subset);
		}
	}
}

class MapFormatter implements CellFormatter {
	private readonly sub: CellFormatter;
	private readonly map: Record<string, any>;
	private readonly defaultValue: any;

	constructor(f:MapFormatterData) {
		this.sub = formatterOf(f.format);
		this.map = f.map;
		this.defaultValue = f.defaultValue || null;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		const mapped:any[] = [];
		for (let i = 0; i < data.length; i++) {
			const d:any = data[i];
			if (d == null) mapped[i] = this.defaultValue;
			else {
				const m:any = this.map[d.toString()];
				mapped[i] = m == null ? this.defaultValue : m;
			}
		}
		return this.sub.format(ctx, ...mapped);
	}
}


class ListFormatter implements CellFormatter {
	private readonly ls: ReturnType<typeof useListStore>;
	private readonly listName: string;

	constructor(f:ListFormatterData) {
		this.ls = useListStore();
		this.listName = f.listName;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const listName = this.listName || ctx.column.listName;
			const l = this.ls.findList(listName);
			const out:VNodeChild[] = [];

			for (let d of data) {
				if (d != null) {
					let val = d.toString();
					const li = findListItem(l, val);
					if (li) out.push(li.name);
					else if (val) out.push("(" + val + ")");
				}
			}
			return out;
		};
	}
}


class ArgFormatter implements CellFormatter {
	private readonly sub: CellFormatter;
	private readonly template: string;

	constructor(f:ArgFormatterData) {
		this.sub = formatterOf(f.format);
		this.template = f.template;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		let t = this.template;
		for (let i = 0; i < data.length; i++) {
			const d:any = data[i];
			const placeholder = "{" + i + "}";
			t = t.replace(placeholder, d == null ? "" : d.toString());
		}
		return this.sub.format(ctx, t);
	}
}


class IfFormatter implements CellFormatter {
	private readonly trueFormat: CellFormatter;
	private readonly falseFormat: CellFormatter | null;
	private readonly test: IfFormatterData['test'];

	constructor(f:IfFormatterData) {
		this.trueFormat = formatterOf(f.trueFormat);
		this.falseFormat = f.falseFormat ? formatterOf(f.falseFormat) : null;
		this.test = f.test ?? null;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		const result = this.test ? testCondition(this.test, ctx.object, data) : !!data[0];
		if (result) return this.trueFormat.format(ctx, ...data);
		else {
			if (this.falseFormat == null) return () => [];
			return this.falseFormat.format(ctx, ...data);
		}
	}
}


class PopFormatter implements CellFormatter {
	private readonly sub: CellFormatter;
	private readonly count: number;

	constructor(f:PopFormatterData) {
		this.sub = formatterOf(f.format);
		this.count = f.count || 1;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		if (this.count > data.length) return this.sub.format(ctx);
		const newLen = data.length - this.count;
		const newData:any[] = [];
		for (let i = 0; i < newLen; i++) {
			newData[i] = data[this.count + i];
		}
		return this.sub.format(ctx, ...newData);
	}
}


class WithFormatter implements CellFormatter {
	private readonly sub: CellFormatter;
	private readonly data: any[];

	constructor(f:WithFormatterData) {
		this.sub = formatterOf(f.format);
		this.data = f.data;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return this.sub.format(ctx, ...this.data);
	}
}


class UserFormatter implements CellFormatter {
	private readonly ls: ReturnType<typeof useListStore>;

	constructor(f:UserFormatterData) {
		this.ls = useListStore();
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			const listName:string = ctx.column.listName;
			const l:Ref<StoreList>|null = listName ? this.ls.findList(listName) : null;
			for (let d of data) {
				if (d == null) continue;
				let usrId = d.toString();
				if (l) {
					// Use list to get user's name
					const li = findListItem(l, usrId);
					if (li) out.push(li.name);
					else out.push(usrId);
				} else {
					out.push(usrId);	// Lookup without a list
				}
			}
			return out;
		};
	}
}


class ActionFormatter implements CellFormatter {
	private readonly sub:CellFormatter;
	private readonly action:string;
	private readonly srcType:number;
	private readonly params:ActionParam[];

	constructor(f:ActionFormatterData) {
		this.sub = formatterOf(f.format);
		this.action = f.action;
		this.srcType = f.srcType;
		this.params = f.params || [];
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		const pars: Record<string, any> = {};
		for (let p of this.params) {
			switch (p.source) {
				case "ARG":
					if (typeof p.index === 'number') {
						if (p.index >= 0 && p.index < data.length) {
							pars[p.name] = data[p.index];
						}
					}
					break;
				case "OBJECT":
					if (typeof p.path === 'string') {
						const o = ctx.object;
						const v = getter(o, p.path);
						if (typeof v !== 'undefined') pars[p.name] = v;
					}
					break;
				case "STATIC":
					if (typeof p.value !== 'undefined') pars[p.name] = p.value;
					break;
			}
		}

		const content = this.sub.format(ctx, ...data);
		return ctx.actionLink(content, this.action, this.srcType, pars);
	}
}


class DefaultFormatter implements CellFormatter {

	private df: CellFormatter;

	constructor(f:DefaultFormatterData) {
		this.df = null;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		if (this.df == null) {
			this.df = formatterOf(buildDefaultFormatter(ctx.column));
		}
		return this.df.format(ctx, ...data);
	}
}

function findListItem(list:Ref<StoreList>, value:string):StoreListItem|null {
	for (let item of list.value) {
		if (item.value === value) return item;
	}
	return null;
}

function rowStyle(list:Ref<StoreList>, value:string, scope:string|null):CSSProperties {
	const item = findListItem(list, value);
	if (item?.specProps) return propsToStyle(item.specProps, scope);
	return {};
}

function styleToString(style: CSSProperties): string {
	return Object.entries(style)
		.filter(([_, v]) => v != null && v !== "")
		.map(([k, v]) => {
			const kebab = k.replace(/[A-Z]/g, m => "-" + m.toLowerCase());
			return `${kebab}:${v}`;
		})
		.join(";");
}

class TagFormatter implements CellFormatter {

	private readonly sub: CellFormatter;
	private readonly scope: string|null;
	private readonly ls: ReturnType<typeof useListStore>;

	constructor(f:TagFormatterData) {
		this.sub = formatterOf(f.format);
		this.scope = f.scope || null;
		this.ls = useListStore();
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			const listName:string = ctx.column.listName;
			const content = this.sub.format(ctx, ...data)();
			if (listName == null || content.length == 0 || data[0] == null) return content;
			const list = this.ls.findList(listName);
			let style:CSSProperties = rowStyle(list, data[0].toString(), this.scope);

			out.push(h('span', { class: 'stateTag', style: style }, content));
			return out;
		};
	}
}

function toMinutes(amount:number, unit: DurationUnit):number {
	switch (unit) {
		case "NANOSECONDS": return amount / 60000000000;
		case "MICROSECONDS": return amount / 60000000;
		case "MILLISECONDS": return amount / 60000;
		case "SECONDS": return amount / 60;
		case "MINUTES": return amount;
		case "HOURS": return amount * 60;
		case "DAYS": return amount * 24 * 60;
	}
	return 0;
}

// Time constants (in minutes)
export const HOUR = 60;
export const WORK_DAY = 8 * HOUR;
export const WORK_WEEK = 5 * WORK_DAY;

/**
 * Converts time specified in minutes to string representation.
 * For example: 100 is converted to "1h 40m".
 * "h" is for hours, "m" for minutes, "d" for working days, "w" for working weeks
 * This method uses business calendar 1w = 5d, 1d = 8h, 1h = 60m
 */
export function minutes2str(minutes: number): string {
	if (minutes === 0) return "0h";

	const weeks = Math.floor(minutes / WORK_WEEK);
	let rest = minutes % WORK_WEEK;

	const days = Math.floor(rest / WORK_DAY);
	rest = rest % WORK_DAY;

	const hours = Math.floor(rest / HOUR);
	const mins = rest % HOUR;

	let buff = "";

	if (weeks > 0) buff += `${weeks}w`;
	if (days > 0) buff += `${buff ? " " : ""}${days}d`;
	if (hours > 0) buff += `${buff ? " " : ""}${hours}h`;
	if (mins > 0) buff += `${buff ? " " : ""}${mins}m`;

	return buff;
}

class DurationFormatter implements CellFormatter {

	private readonly unit: DurationUnit;

	constructor(f:DurationFormatterData) {
		this.unit = f.unit || "MINUTES";
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let d of data) {
				if (d == null) continue;
				if (typeof d !== "number" || Number.isNaN(d)) out.push(NAN_FRAGMENT);
				else {
					const result = minutes2str(toMinutes(d, this.unit));
					out.push(result);
				}
			}
			return out;
		};
	}
}

class PackFormatter implements CellFormatter {

	private readonly sub: CellFormatter;
	private readonly rules: PackFormatterRule[];

	constructor(f:PackFormatterData) {
		this.sub = formatterOf(f.format);
		this.rules = f.rules;
	}

	pack(r:PackFormatterRule, ...data:any):any {
		switch (r.type) {
			case "VALUE": return r.value;
			case "TAKE":
				if (r.index >= 0 && r.index < data.length) return data[r.index];
				else return r.value;
			case "PROPERTY":
				let base:any;
				if (r.index >= 0 && r.index < data.length) base = data[r.index];
				else return r.value;
				if (base == null) return r.value;
				return getter(base, r.property);
		}
		return null;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		const subset:any[] = [];
		for (let i = 0; i < this.rules.length; i++) {
			subset[i] = this.pack(this.rules[i], ...data);
		}
		return this.sub.format(ctx, ...subset);
	}
}

class EnumFormatter implements CellFormatter {

	private readonly map: Record<string, FragmentData[]>;

	constructor(f:EnumFormatterData) {
		this.map = f.map;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => [];
	}
}

class I18Formatter implements CellFormatter {

	private readonly html: boolean;
	private readonly env:FormEnvironment;

	constructor(f:I18nFormatterData) {
		this.html = !!f.html;
		this.env = injectEnv();
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			if (data.length === 0) return [];
			const args:any[] = [];
			for (let i = 1; i < data.length; i++) {
				args.push(data[i]);
			}
			return [this.env.msg(data[0].toString(), ...args)];
		};
	}
}

class ExpandFormatter implements CellFormatter {

	private readonly sub: CellFormatter;

	constructor(f:ExpandFormatterData) {
		this.sub = formatterOf(f.format);
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => [];
	}
}


class ConcatFormatter implements CellFormatter {
	private readonly subs: CellFormatter[];

	constructor(f:ConcatFormatterData) {
		this.subs = f.inputs.map(formatterOf);
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const out:VNodeChild[] = [];
			for (let sub of this.subs) {
				out.push(...sub.format(ctx, ...data)());
			}
			return out;
		};
	}
}


class LinkFormatter implements CellFormatter {
	private readonly sub: CellFormatter;
	private readonly internal: boolean;

	constructor(f:LinkFormatterData) {
		this.sub = formatterOf(f.format);
		this.internal = !!f.internal;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const url = data.length > 0 && data[0] != null ? data[0].toString() : null;
			const content = this.sub.format(ctx, ...data)();
			if (url == null) return content;
			const attrs:Record<string, any> = { href: url };
			if (!this.internal) attrs.target = "_blank";
			return [h('a', attrs, content)];
		};
	}
}


class WrapFormatter implements CellFormatter {
	private readonly sub: CellFormatter;
	private readonly tag: string;
	private readonly attributes: Record<string, string> | null;

	constructor(f:WrapFormatterData) {
		this.sub = formatterOf(f.format);
		this.tag = f.tag;
		this.attributes = f.attributes ?? null;
	}

	format(ctx:CellContext, ...data:any):SlotFactory {
		return () => {
			const content = this.sub.format(ctx, ...data)();
			const attrs:Record<string, any> = this.attributes ? { ...this.attributes } : {};
			return [h(this.tag, attrs, content)];
		};
	}
}


// export abstract class CellFormatterBase {
// 	protected in: FormatterInput[] | undefined;
// 	protected args: any[] | null;
//
// 	constructor(f:HasFormatterInput) {
// 		this.in = f.in;
// 		this.args = null;
// 	}
//
// 	/**
// 	 * Evaluate input arguments for this formatter
// 	 */
// 	arguments(data:Map<string, any>):any[] {
// 		if (this.args == null) {
// 			const args = Array.isArray(this.in) ? this.in : ["$0"];
// 			this.args = [];
// 			for (let fi of args) {
// 				let val: any;
// 				if (typeof fi === "string") {
// 					// This is variable name
// 					val = data.get(fi);
// 				} else {
// 					// This is input from another formatter
// 					val = formatterOf(fi).format(data);
// 				}
// 				this.args.push(val === undefined ? null : val);
// 			}
// 		}
// 		return this.args;
// 	}
//
// 	/**
// 	 * Get Nth argument
// 	 * @param data  all values extracted
// 	 * @param idx   index of argument
// 	 */
// 	argument(data: Map<string, any>, idx: number): any {
// 		return this.arguments(data)[idx];
// 	}
//
// 	hasArgument(data: Map<string, any>, idx:number):boolean {
// 		const vals = this.arguments(data);
// 		return vals.length > idx;
// 	}
//
// 	abstract process(data:Map<string, any>):void;
// 	abstract format(data:Map<string, any>):any;
// }
//
//
// export class HtmlFormatterBase extends FormatterBase {
//     private out: string | undefined;
//
//     constructor(f:CellFormatter) {
// 		super(f);
//         this.out = f.out;
//     }
//
//     output(data:Map<string, any>, result:any):void {
//         data.set(this.out ? this.out : "value", result);
//     }
//
//     process(data:Map<string, any>) {
//     }
//
//     format(data:Map<string, any>):any {
//         this.process(data);
//         return data.get(this.out ? this.out : "value");
//     }
// }
//
// export class CellComponentFormatter extends FormatterBase {
// 	constructor(f:ComponentCell) {
// 		super(f);
// 	}
//
// 	output(data:Map<string, any>, result:any):void {
// 		data.set("html", result);
// 	}
//
// 	process(data:Map<string, any>) {
// 		// Only evaluate
// 		const out = new SafeHtml();
// 		for (let value of this.arguments(data)) {
// 			out.append(value);
// 		}
// 		this.output(data, out);
// 	}
//
// 	format(data:Map<string, any>):any {
// 		this.process(data);
// 		return data.get("html");
// 	}
// }
//
//
// class Join extends HtmlFormatterBase {
//     private separator: string;
//     constructor(f: JoinFormatter) {
//         super(f);
//         this.separator = f.separator == null ? " " : f.separator;
//     }
//
//     process(data: Map<string, any>) {
//         const out = new SafeHtml();
//         let idx = 0;
//         for (let value of this.arguments(data)) {
//             out.append(value);
//             if (idx > 0) out.appendHtml(this.separator);
//             idx++;
//         }
//         this.output(data, out);
//     }
// }
//
// class Money extends HtmlFormatterBase {
//     private options: object;
//     constructor(f: MoneyFormatter) {
//         super(f);
//         this.options = f.options ? f.options : {};
//     }
//
//     process(data: Map<string, any>) {
//         const ops:any = {
//             ...this.options
//         };
//         const amount = this.argument(data, 0);
//         if (this.hasArgument(data,1)) {
//             const currency = this.argument(data, 1);
//             ops.style = "currency";
//             ops.currency = currency;
//         }
//         const result = new Intl.NumberFormat(navigator.language, ops).format(amount);
//         this.output(data, result);
//     }
// }
//
// class Const extends FormatterBase {
// 	private value:any;
//
// 	constructor(f: ConstFormatter) {
// 		super(f);
// 		this.value = f.value;
// 	}
//
//     format(data: Map<string, any>):any {
//         return this.value;
//     }
//     process(data: Map<string, any>): void {
//         data.set("value", this.value);
//     }
// }
//
// const DATE_OPS:Intl.DateTimeFormatOptions = {
//     day: "numeric",
//     month: "numeric",
//     year: "numeric"
// };
//
// const DATE_TIME_OPS:Intl.DateTimeFormatOptions = {
//     day: "numeric",
//     month: "numeric",
//     year: "2-digit",
//     hour: "numeric",
//     minute: "numeric",
//     weekday: "short"
// };
//
// class Temporal extends HtmlFormatterBase {
//     private options: object;
//     constructor(f: DateFormatter) {
//         super(f);
//         this.options = f.options ? f.options : {};
//     }
//
//     process(data: Map<string, any>) {
//         const value = this.argument(data, 0);
//         let result:string;
//         if (value == null) result = "";
//         else {
//             if (typeof value === "string") {
//                 const parsed = parseDate(value);
//                 if (parsed.date == null) result = "";
//                 else {
//                     let ops;
//                     if (parsed.wholeDay) {
//                         ops = {
//                             ...DATE_OPS,
//                             ...this.options
//                         }
//                     } else {
//                         ops = {
//                             ...DATE_TIME_OPS,
//                             ...this.options
//                         }
//                     }
//                     result = new Intl.DateTimeFormat(navigator.language, ops).format(parsed.date);
//                 }
//             } else if (value instanceof Date) {
//                 result = new Intl.DateTimeFormat(navigator.language, { ...DATE_OPS, ...this.options }).format(value);
//             } else result = "";
//         }
//
//         if (result.length > 0) result = "<span class='nw'>"+result+"</span>";
//
//         this.output(data, result);
//     }
// }
//
// class Sequence extends HtmlFormatterBase {
//     private sequence: CellFormatter[];
//     constructor(f: SequenceFormatter) {
//         super(f);
//         this.sequence = f.seq;
//     }
//
//     process(data: Map<string, any>) {
//         let idx = 0;
//         let value:any = null;
//         for (let cf of this.sequence) {
//             const f = formatterOf(cf);
//             if (idx === this.sequence.length - 1) {
//                 value = f.format(data)
//             } else f.process(data);
//             idx++;
//         }
//         if (idx > 0) this.output(data, value);
//     }
// }
//
//
// class List extends HtmlFormatterBase {
//     private list: string | FormList;
//     private ifmissing: string | undefined;
//     private escape: boolean;
//
//     constructor(f: ListFormatter, escapeOutput = true) {
//         super(f);
//         this.list = f.list;
//         this.ifmissing = f.ifmissing;
//         this.escape = escapeOutput;
//     }
//
//
//     process(data: Map<string, any>) {
//         let listArray:Ref<FormList>;
//         if (Array.isArray(this.list)) listArray = ref(this.list);
//         else {
//             const findList = data.get("$findList") as FindListFunc;
//             listArray = findList(this.list);
//         }
//         let value = this.argument(data, 0);
//         if (value == null) value = "";
//         else if (typeof value !== "string") {
//             // list support only string values
//             value = cast("string", value);
//         }
//         let li = listArray.value.find((item) => item.value === value);
//         if (!li) {
//             if (this.ifmissing !== undefined) {
//                 li = { value: value, name: this.ifmissing };
//             } else {
//                 li = { value: value, name: "("+value+")" };
//             }
//         }
//         this.output(data, this.escape ? escapeHtml(li.name) : li.name);
//     }
// }
//
// class IconFmt extends HtmlFormatterBase {
//     constructor(f: IconFormatter) {
//         super(f);
//     }
//
//     process(data: Map<string, any>) {
//         const icon = this.argument(data, 0);
//         const res = new SafeHtml(Icon.fi(icon));
//         this.output(data, res);
//     }
// }
//
// class Bool extends HtmlFormatterBase {
//     constructor(f: BooleanFormatter) {
//         super(f);
//     }
//
//     process(data: Map<string, any>) {
//         const boolValue = !!this.argument(data, 0);
//         const res = boolValue ? new SafeHtml(Icon.fi("yes")) : "";
//         this.output(data, res);
//     }
// }